import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import 'react-calendar/dist/Calendar.css';
import '../../../styles/date-ranger-picker.css';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import { crmService } from '../../../services/central-api/crm';
import BasicModal from '../../../components/BasicModal';
import MyDropDownPicker from '../../../components/MyDropDownPicker';
import FormInput from '../../../components/FormInput';
import { MyCheckbox } from '../../../components/MyCheckbox';
import { useForm } from 'react-hook-form';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    cameraId: string;
}

interface PurgeCameraFormFields {
    reason: string;
    force: boolean;
    serverPoolId: string;
}

const DEFAULT_FORM_VALUES: PurgeCameraFormFields = {
    reason: '',
    force: false,
    serverPoolId: '',
};

export default function PurgeCameraModal({ isModalVisible, setModalVisible, cameraId }: ModalProps) {
    const [serverPoolDropdownOpen, setServerPoolDropdownOpen] = useState(false);
    const [availableServerPools, setAvailableServerPools] = useState<{ id: string; }[]>([]);

    const form = useForm<PurgeCameraFormFields>({
        defaultValues: DEFAULT_FORM_VALUES,
        mode: 'onChange',
    });

    const reason = form.watch('reason');
    const force = form.watch('force');
    const serverPoolId = form.watch('serverPoolId');

    function onClean() {
        setModalVisible(false);
        setAvailableServerPools([]);
        setServerPoolDropdownOpen(false);
        form.reset(DEFAULT_FORM_VALUES);
    }

    async function onSubmit({ reason, force, serverPoolId }: PurgeCameraFormFields) {
        try {
            await crmService.moveCameraToAnotherServerPool(cameraId, serverPoolId, {
                reason: reason,
                force,
            });
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            onClean();
        } catch (error) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function getAvailableServerPools() {
        try {
            const resp = await crmService.getCameraAvailableServerPools(cameraId);
            setAvailableServerPools(resp);
        } catch (error) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            getAvailableServerPools();
        }
    }, [isModalVisible, cameraId]);

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            title={translate('selectServerPoolToPurgeCamera')}
            width='small'
            mobileHeight='fill'
            primaryButton={{
                text: translate('confirm'),
                onClick: form.handleSubmit(onSubmit),
                disabled: !serverPoolId || !reason.trim(),
            }}
            disableScrollView={true}
        >
            <MyDropDownPicker
                placeholder={translate('serverPools')}
                searchable={true}
                items={availableServerPools.map(sp => ({
                    value: sp.id,
                    label: sp.id,
                }))}
                open={serverPoolDropdownOpen}
                setOpen={setServerPoolDropdownOpen}
                setValue={callback => {
                    const value = typeof callback === 'function'
                        ? callback(serverPoolId)
                        : callback;

                    form.setValue('serverPoolId', value ?? '', {
                        shouldValidate: true,
                    });
                }}
                value={serverPoolId}
                zIndex={3}
            />

            <FormInput
                label={translate('reason')}
                placeholder={translate('reason')}
                value={reason}
                onChangeText={value => {
                    form.setValue('reason', value, {
                        shouldValidate: true,
                    });
                }}
                required={true}
                semiBoldLabel
            />

            <View style={{ height: 40 }}>
                <MyCheckbox
                    label={translate('force')}
                    checked={force}
                    setChecked={value => {
                        form.setValue('force', value);
                    }}
                />
            </View>
        </BasicModal>
    );
}
